import React, { Fragment, FC, ReactNode } from 'react'
import { View, StyleSheet } from 'react-native'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import _ from 'lodash'
import { moderateScale } from '@partipost-cm-new/app-preview/dist/Utils/Scaling'
import { color, spacing, fontSize, fontFamily, fontWeight } from '@partipost-cm-new/app-preview/dist/Styles/Base'
import PartiText from '@partipost-cm-new/app-preview/dist/Components/PartiText'
import ListDivider from '@partipost-cm-new/app-preview/dist/Components/ListDivider'
import { getDisplayInArray, ArrayItem } from '@partipost-cm-new/app-preview/dist/Utils'
import Images from '@partipost-cm-new/app-preview/dist/Constants/Images'
import { abbreviateNumber } from '@partipost-cm-new/app-preview/dist/Utils/index'
import { PROFILE_REQUIREMENT, SOCIAL_MEDIA_PLATFORM } from '@partipost-cm-new/app-preview/dist/Constants'
import Icon from 'particles/Icon'
import { TFunction } from 'i18next'
import { COUNTRY_ENUM, OccupationOption } from 'services/api/constants'
import { REQUIREMENT_TYPE_ENUM, RequirementState, FollowerState, LocationState } from 'state/campaigns'
import { selectCampaignByTemplateData } from 'state'
import { selectOccupationOptions, selectProfileTypeOptions } from 'state/constants'

const getRequirements = (t: TFunction) => {
    return {
        [PROFILE_REQUIREMENT.AGE]: {
            label: t('campaignRequirement.ageHeader', 'Age'),
            icon: Images.profileAge,
        },
        [PROFILE_REQUIREMENT.GENDER]: {
            label: t('campaignRequirement.genderHeader', 'Gender'),
            icon: Images.profileGender,
        },
        [PROFILE_REQUIREMENT.INSTAGRAM]: {
            label: t('campaignRequirement.instagramFollowersHeader', 'IG Followers'),
            icon: Images.profileInstagram,
        },
        [PROFILE_REQUIREMENT.FACEBOOK]: {
            label: t('campaignRequirement.facebookFriendsHeader', 'Facebook Friends'),
            icon: Images.profileFacebook,
        },
        [PROFILE_REQUIREMENT.TWITTER]: {
            label: t('campaignRequirement.twitterFollowersHeader', 'Twitter Followers'),
            icon: Images.profileTwitter,
        },
        [PROFILE_REQUIREMENT.TIKTOK]: {
            label: t('campaignRequirement.tiktokFollowersHeader', 'Tik-Tok Followers'),
            icon: Images.profileTiktok,
        },
        [PROFILE_REQUIREMENT.LOCATION]: {
            label: t('campaignRequirement.locationHeader', 'Location'),
            icon: Images.profileLocation,
        },
        [PROFILE_REQUIREMENT.MARITAL_STATUS]: {
            label: t('campaignRequirement.maritalStatusHeader', 'Marital Status'),
            icon: Images.profileMaritalStatus,
        },
        [PROFILE_REQUIREMENT.OCCUPATION]: {
            label: t('campaignRequirement.occupationHeader', 'Occupation'),
            icon: Images.profileOccupation,
        },
        [PROFILE_REQUIREMENT.CONTENT_TYPE]: {
            label: t('campaignRequirement.contentTypeHeader', 'Content Type'),
            icon: Images.profileContentType,
        },
        [PROFILE_REQUIREMENT.CAMPAIGN_HISTORY]: {
            label: t('campaignRequirement.contentTypeHeader', 'Campaign History'),
            icon: Images.profileCampaignHistory,
        },
    }
}

interface BadgeContainerProps {
    children: ReactNode
    text?: string
}

const BadgeContainer: FC<BadgeContainerProps> = ({ children, text }) => {
    return (
        <View style={styles.mainContainer}>
            <View style={styles.badgeContainer}>{children}</View>
            <PartiText style={styles.badgeText} numberOfLines={2} ellipsizeMode="tail">
                {text}
            </PartiText>
        </View>
    )
}

interface RequirementProps {
    badgeLabel?: string | null
    label?: string
    icon?: string
}

interface IconListProps {
    requirements: RequirementProps[]
}

const IconList: FC<IconListProps> = ({ requirements }) => {
    return (
        <View style={styles.iconContainer}>
            {requirements.map(item => {
                const { label, badgeLabel, icon } = item
                return (
                    <View key={'icon' + label} style={styles.modalRequirementContainer}>
                        <BadgeContainer text={badgeLabel || label}>
                            <img
                                src={icon}
                                style={{
                                    height: moderateScale(42),
                                    width: moderateScale(42),
                                }}
                            />
                        </BadgeContainer>
                    </View>
                )
            })}
        </View>
    )
}

const getGenderText = (t: TFunction, gender: string) => {
    return t(`gender.${gender.toLowerCase()}`)
}

const getRequirementForFollowers = (t: TFunction, follower: FollowerState, country: COUNTRY_ENUM) => {
    const requirements = getRequirements(t)
    const { platform, min: followerMin, max: followerMax } = follower
    let requirement
    switch (platform) {
        case SOCIAL_MEDIA_PLATFORM.IG:
            requirement = requirements[PROFILE_REQUIREMENT.INSTAGRAM]
            break
        case SOCIAL_MEDIA_PLATFORM.FB:
            requirement = requirements[PROFILE_REQUIREMENT.FACEBOOK]
            break
        case SOCIAL_MEDIA_PLATFORM.TIKTOK:
            requirement = requirements[PROFILE_REQUIREMENT.TIKTOK]
            break
        case SOCIAL_MEDIA_PLATFORM.TWITTER:
            requirement = requirements[PROFILE_REQUIREMENT.TWITTER]
            break
    }
    const badgeLabel =
        followerMin && followerMax
            ? t('appCampaign.campaignDetails.followersRangeRequirement', {
                  min: abbreviateNumber(followerMin, country),
                  max: abbreviateNumber(followerMax, country),
              })
            : followerMin
              ? t('appCampaign.campaignDetails.minimumFollowersRequirement', {
                    min: abbreviateNumber(followerMin, country),
                })
              : followerMax
                ? t('appCampaign.campaignDetails.maximumFollowersRequirement', {
                      max: abbreviateNumber(followerMax, country),
                  })
                : null
    return {
        requirement: {
            ...requirement,
            badgeLabel,
        },
    }
}

export const getLocationDisplay = (location: LocationState) => {
    const names: string[] = []
    const locationKeys = Object.keys(location) as (keyof LocationState)[]
    locationKeys.forEach((key: keyof LocationState) => {
        const detail = location[key]
        if (detail && detail.name) {
            names.push(detail.name)
        }
    })
    return names.join(', ')
}

const getRequirement = (t: TFunction, campaignRequirement: RequirementState, occupations: OccupationOption[]) => {
    const requirements = getRequirements(t)
    const { type, properties } = campaignRequirement
    let requirement, badgeLabel
    switch (type) {
        case REQUIREMENT_TYPE_ENUM.AGE:
            const { min: ageMin, max: ageMax } = properties
            requirement = requirements[PROFILE_REQUIREMENT.AGE]
            badgeLabel =
                ageMin && ageMax
                    ? t('appCampaign.campaignDetails.ageRangeRequirement', { min: ageMin, max: ageMax })
                    : ageMin
                      ? t('appCampaign.campaignDetails.minimumAgeRequirement', { min: ageMin })
                      : ageMax
                        ? t('appCampaign.campaignDetails.maximumAgeRequirement', { max: ageMax })
                        : null

            requirement = {
                ...requirement,
                badgeLabel,
            }
            break
        case REQUIREMENT_TYPE_ENUM.GENDER:
            const { value: gender } = properties
            if (gender) {
                badgeLabel = t('appCampaign.campaignDetails.genderRequirement', { gender: getGenderText(t, gender) })
                requirement = {
                    ...requirements[PROFILE_REQUIREMENT.GENDER],
                    badgeLabel,
                }
            }
            break
        case REQUIREMENT_TYPE_ENUM.OCCUPATION:
            const { occupations: occupationRequirements } = properties
            if (occupationRequirements) {
                const values = occupationRequirements && occupationRequirements.map(item => item.value)
                badgeLabel = getDisplayInArray(values, occupations)
                requirement = {
                    ...requirements[PROFILE_REQUIREMENT.OCCUPATION],
                    badgeLabel,
                }
            }
            break
        case REQUIREMENT_TYPE_ENUM.LOCATION:
            const { locations: locationRequirements } = properties
            const labels: string[] = []
            locationRequirements && locationRequirements.forEach(item => labels.push(getLocationDisplay(item)))
            badgeLabel = labels.join(',')
            requirement = {
                ...requirements[PROFILE_REQUIREMENT.LOCATION],
                badgeLabel,
            }
            break
        default:
            requirement = null
    }
    return { requirement }
}

const Requirements: FC = () => {
    const [t] = useTranslation()
    const campaign = useSelector(selectCampaignByTemplateData)
    const occupationOptions = useSelector(selectOccupationOptions)
    const profileTypeOptions = useSelector(selectProfileTypeOptions)

    let otherRestriction, profileTypes
    const requirementList: RequirementProps[] = []

    // TODO: extends this to support blocked campaign in editor preview flow
    if (!campaign || _.isEmpty(campaign.requirements)) return null

    campaign.requirements &&
        campaign.requirements.forEach((item: RequirementState) => {
            if (item.type === REQUIREMENT_TYPE_ENUM.OTHERS) {
                otherRestriction = item.properties.value
            } else if (item.type === REQUIREMENT_TYPE_ENUM.FOLLOWER) {
                const { followers } = item.properties
                if (followers) {
                    followers.forEach(follower => {
                        const { requirement } = getRequirementForFollowers(t, follower, campaign.country)
                        requirementList.push(requirement)
                    })
                }
            } else if (item.type === REQUIREMENT_TYPE_ENUM.PROFILE_TYPE) {
                const { profile_types } = item.properties
                if (profile_types) {
                    profileTypes = getDisplayInArray(
                        profile_types && profile_types.map(profileType => profileType.value),
                        profileTypeOptions as ArrayItem[],
                    )
                }
            } else {
                const { requirement } = getRequirement(t, item, occupationOptions)
                if (requirement) {
                    requirementList.push(requirement)
                }
            }
        })

    return (
        <Fragment>
            <View style={{ paddingHorizontal: spacing.medium }}>
                <View style={styles.requirementContainer}>
                    <PartiText style={styles.title}>
                        {t('appCampaign.campaignDetails.campaignRequirementTitle')}
                    </PartiText>
                    <View
                        style={{
                            paddingBottom: spacing.small,
                            paddingLeft: spacing.extraSmall,
                        }}
                    >
                        <Icon size="16px" name={'question'} color={'actionActive'} />
                    </View>
                </View>
                <IconList requirements={requirementList} />
                {profileTypes ? (
                    <View style={otherRestriction ? styles.otherRequirementGap : null}>
                        <PartiText style={styles.profileTypeTitle}>
                            {t('campaignEditor.requirement.profileTypeSectionMenuHeader')}
                        </PartiText>
                        <PartiText>{profileTypes}</PartiText>
                    </View>
                ) : null}
                {otherRestriction ? <PartiText>{otherRestriction}</PartiText> : null}
            </View>
            <ListDivider style={styles.listDivider} />
        </Fragment>
    )
}

const styles = StyleSheet.create({
    listDivider: {
        marginHorizontal: spacing.medium,
        marginVertical: spacing.medium,
    },
    iconContainer: { flexDirection: 'row', flexWrap: 'wrap', paddingVertical: spacing.small },
    requirementContainer: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    badgeText: {
        textAlign: 'center',
        paddingTop: spacing.small,
        fontSize: fontSize.extraSmall,
        color: color.astronaut,
    },
    title: {
        fontFamily: fontFamily.title,
        fontWeight: fontWeight.bold,
        fontSize: fontSize.extraLarge,
        paddingBottom: spacing.small,
    },
    otherRequirementGap: {
        paddingBottom: spacing.small2,
    },
    profileTypeTitle: {
        fontFamily: fontFamily.title,
        fontWeight: fontWeight.bold,
        paddingBottom: spacing.extraSmall,
    },
    mainContainer: {
        alignItems: 'center',
        paddingVertical: spacing.small,
    },
    badgeContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        height: moderateScale(50),
        width: moderateScale(50),
        borderRadius: moderateScale(25),
        borderColor: color.dodgerBlue,
        borderWidth: 1,
    },
    modalRequirementContainer: {
        width: '25%',
    },
})

export default Requirements
